import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  FileCode,
  ShieldCheck,
  ShieldX,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Server,
  ChevronDown,
  ChevronUp,
  Globe,
  Eye,
  EyeOff,
  Copy,
  Check,
  AlertOctagon,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { GradeCard } from "../ModuleScore";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// Headers Module Data Types
interface HstsHeader {
  max_age?: number;
  include_subdomains?: boolean;
  preload?: boolean;
}

interface CspHeader {
  raw?: string;
  allows_unsafe_inline?: boolean;
  allows_unsafe_eval?: boolean;
  directives?: Record<string, string[]>;
}

interface SecurityHeaders {
  strict_transport_security?: HstsHeader | string;
  content_security_policy?: CspHeader | string;
  x_frame_options?: string;
  x_content_type_options?: string;
  x_xss_protection?: string;
  referrer_policy?: string;
  permissions_policy?: string;
  cross_origin_opener_policy?: string;
  cross_origin_embedder_policy?: string;
  cross_origin_resource_policy?: string;
}

interface HeaderHostResult {
  url: string;
  status_code?: number;
  headers?: SecurityHeaders;
  all_headers?: Record<string, string>;
  issues?: Array<{ code?: string; message?: string } | string>;
}

export interface HeadersModuleData {
  overall_score?: number;
  grade?: string;
  hosts_scanned?: number;
  hosts_reachable?: number;
  results?: HeaderHostResult[];
  issues?: Array<string | Issue>;
}

export interface HeadersModuleViewProps {
  data: HeadersModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Header status indicator
function HeaderStatus({
  present,
  secure,
  label,
}: {
  present: boolean;
  secure?: boolean;
  label: string;
}) {
  if (!present) {
    return (
      <div className="flex items-center gap-2">
        <XCircle className="h-4 w-4 text-red-400" />
        <span className="text-sm text-muted-foreground">{label}</span>
        <Badge
          variant="outline"
          className="text-xs bg-red-500/10 border-red-500/30 text-red-400"
        >
          Missing
        </Badge>
      </div>
    );
  }

  if (secure === false) {
    return (
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-400" />
        <span className="text-sm">{label}</span>
        <Badge
          variant="outline"
          className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
        >
          Weak
        </Badge>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
      <span className="text-sm">{label}</span>
      <Badge
        variant="outline"
        className="text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
      >
        Present
      </Badge>
    </div>
  );
}

// HSTS details
function HstsDetails({ hsts }: { hsts?: HstsHeader | string }) {
  if (!hsts) {
    return (
      <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/30">
        <div className="flex items-center gap-2 text-red-400">
          <ShieldX className="h-4 w-4" />
          <span className="font-medium">HSTS Not Configured</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Enable Strict-Transport-Security to enforce HTTPS
        </p>
      </div>
    );
  }

  if (typeof hsts === "string") {
    return (
      <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/30">
        <div className="flex items-center gap-2 text-emerald-400 mb-2">
          <ShieldCheck className="h-4 w-4" />
          <span className="font-medium">HSTS Enabled</span>
        </div>
        <code className="text-xs font-mono text-muted-foreground break-all">
          {hsts}
        </code>
      </div>
    );
  }

  const maxAgeYears = hsts.max_age ? Math.floor(hsts.max_age / 31536000) : 0;
  const isStrongMaxAge = hsts.max_age && hsts.max_age >= 31536000;

  return (
    <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
      <div className="flex items-center gap-2 text-emerald-400">
        <ShieldCheck className="h-4 w-4" />
        <span className="font-medium">HSTS Enabled</span>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">max-age</p>
          <p
            className={cn(
              "font-mono text-sm",
              isStrongMaxAge ? "text-emerald-400" : "text-amber-400"
            )}
          >
            {hsts.max_age?.toLocaleString() || "—"}
            {maxAgeYears > 0 && (
              <span className="text-xs text-muted-foreground ml-1">
                ({maxAgeYears}y)
              </span>
            )}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">includeSubDomains</p>
          <p
            className={cn(
              "font-medium text-sm",
              hsts.include_subdomains ? "text-emerald-400" : "text-muted-foreground"
            )}
          >
            {hsts.include_subdomains ? "Yes" : "No"}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">preload</p>
          <p
            className={cn(
              "font-medium text-sm",
              hsts.preload ? "text-emerald-400" : "text-muted-foreground"
            )}
          >
            {hsts.preload ? "Yes" : "No"}
          </p>
        </div>
      </div>
    </div>
  );
}

// CSP details
function CspDetails({ csp }: { csp?: CspHeader | string }) {
  const [showRaw, setShowRaw] = React.useState(false);

  if (!csp) {
    return (
      <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/30">
        <div className="flex items-center gap-2 text-red-400">
          <ShieldX className="h-4 w-4" />
          <span className="font-medium">CSP Not Configured</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Enable Content-Security-Policy to prevent XSS attacks
        </p>
      </div>
    );
  }

  const rawValue = typeof csp === "string" ? csp : csp.raw;
  const hasUnsafeInline =
    typeof csp === "object" && csp.allows_unsafe_inline;
  const hasUnsafeEval = typeof csp === "object" && csp.allows_unsafe_eval;

  return (
    <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-emerald-400">
          <ShieldCheck className="h-4 w-4" />
          <span className="font-medium">CSP Enabled</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowRaw(!showRaw)}
          className="gap-1 text-xs"
        >
          {showRaw ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showRaw ? "Hide" : "Show"} Raw
        </Button>
      </div>

      {/* Warnings */}
      {(hasUnsafeInline || hasUnsafeEval) && (
        <div className="flex flex-wrap gap-2">
          {hasUnsafeInline && (
            <Badge
              variant="outline"
              className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              unsafe-inline
            </Badge>
          )}
          {hasUnsafeEval && (
            <Badge
              variant="outline"
              className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              unsafe-eval
            </Badge>
          )}
        </div>
      )}

      {/* Raw policy */}
      {showRaw && rawValue && (
        <pre className="p-3 rounded bg-muted/50 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
          {rawValue}
        </pre>
      )}

      {/* Directives */}
      {typeof csp === "object" && csp.directives && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">
            Directives
          </p>
          <div className="grid gap-2">
            {Object.entries(csp.directives).slice(0, 6).map(([key, values]) => (
              <div
                key={key}
                className="flex items-start gap-2 p-2 rounded bg-muted/30"
              >
                <span className="text-xs font-mono text-cyan-400 min-w-[120px]">
                  {key}
                </span>
                <span className="text-xs font-mono text-muted-foreground break-all">
                  {values.join(" ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Host result card
function HostResultCard({ result }: { result: HeaderHostResult }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const headers = result.headers || {};
  const hostIssues = result.issues?.length || 0;

  // Count present headers
  const headerChecks = [
    { key: "strict_transport_security", label: "HSTS" },
    { key: "content_security_policy", label: "CSP" },
    { key: "x_frame_options", label: "X-Frame-Options" },
    { key: "x_content_type_options", label: "X-Content-Type" },
    { key: "referrer_policy", label: "Referrer-Policy" },
    { key: "permissions_policy", label: "Permissions-Policy" },
  ];

  const presentCount = headerChecks.filter(
    (h) => headers[h.key as keyof SecurityHeaders]
  ).length;

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center gap-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
      >
        <Globe className="h-5 w-5 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-sm truncate">{result.url}</p>
        </div>
        {result.status_code && (
          <Badge
            variant="outline"
            className={cn(
              "text-xs",
              result.status_code >= 200 && result.status_code < 300
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                : "bg-amber-500/10 text-amber-400 border-amber-500/30"
            )}
          >
            {result.status_code}
          </Badge>
        )}
        <Badge variant="outline" className="text-xs">
          {presentCount}/{headerChecks.length} headers
        </Badge>
        {hostIssues > 0 && (
          <Badge
            variant="outline"
            className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
          >
            {hostIssues} issue{hostIssues > 1 ? "s" : ""}
          </Badge>
        )}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 border-t border-border space-y-4">
          {/* Security Headers Checklist */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
              Security Headers
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {headerChecks.map((check) => {
                const value = headers[check.key as keyof SecurityHeaders];
                return (
                  <HeaderStatus
                    key={check.key}
                    present={!!value}
                    label={check.label}
                  />
                );
              })}
            </div>
          </div>

          {/* HSTS Details */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
              HSTS Configuration
            </p>
            <HstsDetails hsts={headers.strict_transport_security} />
          </div>

          {/* CSP Details */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
              Content Security Policy
            </p>
            <CspDetails csp={headers.content_security_policy} />
          </div>

          {/* Other Headers */}
          <div className="grid gap-2 sm:grid-cols-2">
            {headers.x_frame_options && (
              <div className="p-3 rounded-lg bg-muted/30 border border-border">
                <p className="text-xs text-muted-foreground mb-1">X-Frame-Options</p>
                <p className="font-mono text-sm">{headers.x_frame_options}</p>
              </div>
            )}
            {headers.referrer_policy && (
              <div className="p-3 rounded-lg bg-muted/30 border border-border">
                <p className="text-xs text-muted-foreground mb-1">Referrer-Policy</p>
                <p className="font-mono text-sm">{headers.referrer_policy}</p>
              </div>
            )}
            {headers.x_content_type_options && (
              <div className="p-3 rounded-lg bg-muted/30 border border-border">
                <p className="text-xs text-muted-foreground mb-1">X-Content-Type-Options</p>
                <p className="font-mono text-sm">{headers.x_content_type_options}</p>
              </div>
            )}
            {headers.permissions_policy && (
              <div className="p-3 rounded-lg bg-muted/30 border border-border">
                <p className="text-xs text-muted-foreground mb-1">Permissions-Policy</p>
                <p className="font-mono text-xs truncate" title={headers.permissions_policy}>
                  {headers.permissions_policy}
                </p>
              </div>
            )}
          </div>

          {/* CORS Headers */}
          {(headers.cross_origin_opener_policy ||
            headers.cross_origin_embedder_policy ||
            headers.cross_origin_resource_policy) && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Cross-Origin Headers
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                {headers.cross_origin_opener_policy && (
                  <div className="p-3 rounded-lg bg-muted/30 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">COOP</p>
                    <p className="font-mono text-xs">{headers.cross_origin_opener_policy}</p>
                  </div>
                )}
                {headers.cross_origin_embedder_policy && (
                  <div className="p-3 rounded-lg bg-muted/30 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">COEP</p>
                    <p className="font-mono text-xs">{headers.cross_origin_embedder_policy}</p>
                  </div>
                )}
                {headers.cross_origin_resource_policy && (
                  <div className="p-3 rounded-lg bg-muted/30 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">CORP</p>
                    <p className="font-mono text-xs">{headers.cross_origin_resource_policy}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Host Issues */}
          {result.issues && result.issues.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Issues
              </p>
              <div className="space-y-2">
                {result.issues.map((issue, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-2 rounded bg-amber-500/5 border border-amber-500/20"
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
                    <span className="text-sm text-amber-400">
                      {typeof issue === "string" ? issue : issue.message || issue.code}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Headers */}
          <RawHeadersToggle allHeaders={result.all_headers} />
        </div>
      )}
    </div>
  );
}

// Parse severity prefix and URL from backend-generated issue strings
// Format: "[HIGH] https://example.com: Some message here"
const SEVERITY_PREFIX_RE = /^\[(CRITICAL|HIGH|MEDIUM|LOW|INFO)\]\s*/i;
const URL_MESSAGE_RE = /^(https?:\/\/\S+?):\s+(.+)$/;

// Convert string issues to Issue objects
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];

  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      // Extract severity from [HIGH]/[MEDIUM]/etc. prefix
      const prefixMatch = issue.match(SEVERITY_PREFIX_RE);
      const severity: Issue["severity"] = prefixMatch
        ? (prefixMatch[1].toLowerCase() as Issue["severity"])
        : "medium";
      const withoutPrefix = prefixMatch ? issue.slice(prefixMatch[0].length) : issue;

      // Split "https://host: description" into asset + message
      const urlMatch = withoutPrefix.match(URL_MESSAGE_RE);
      const asset = urlMatch ? urlMatch[1] : undefined;
      const message = urlMatch ? urlMatch[2] : withoutPrefix;

      return {
        id: `headers-issue-${index}`,
        severity,
        message,
        asset,
      };
    }
    return issue;
  });
}

// Critical security headers that should be present
const CRITICAL_HEADERS: Array<{ key: string; name: string; description: string; severity: "high" | "medium" | "low" }> = [
  {
    key: "strict_transport_security",
    name: "Strict-Transport-Security (HSTS)",
    description: "Enforces HTTPS connections, reducing risk of downgrade attacks",
    severity: "medium" as const,
  },
  {
    key: "content_security_policy",
    name: "Content-Security-Policy (CSP)",
    description: "Adds defense-in-depth against XSS by controlling resource loading",
    severity: "medium" as const,
  },
  {
    key: "x_frame_options",
    name: "X-Frame-Options",
    description: "Controls whether the site can be embedded in third-party frames",
    severity: "low" as const,
  },
  {
    key: "x_content_type_options",
    name: "X-Content-Type-Options",
    description: "Instructs browsers not to MIME-sniff response content types",
    severity: "low" as const,
  },
  {
    key: "referrer_policy",
    name: "Referrer-Policy",
    description: "Controls how much referrer information is shared",
    severity: "low" as const,
  },
  {
    key: "permissions_policy",
    name: "Permissions-Policy",
    description: "Controls browser feature access (camera, microphone, etc.)",
    severity: "low" as const,
  },
];

// Missing Headers Alert Component
function MissingHeadersAlert({ results }: { results: HeaderHostResult[] }) {
  // Aggregate missing headers across all hosts
  const missingHeaders: Record<string, number> = {};

  results.forEach((result) => {
    const headers = result.headers || {};
    CRITICAL_HEADERS.forEach((header) => {
      if (!headers[header.key as keyof SecurityHeaders]) {
        missingHeaders[header.key] = (missingHeaders[header.key] || 0) + 1;
      }
    });
  });

  const missingList = CRITICAL_HEADERS.filter((h) => missingHeaders[h.key]);

  if (missingList.length === 0) return null;

  const highSeverityMissing = missingList.filter((h) => h.severity === "high");

  return (
    <Card className={cn(
      "bg-card/50",
      highSeverityMissing.length > 0 ? "border-red-500/30" : "border-amber-500/30"
    )}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <AlertOctagon className={cn(
            "h-4 w-4",
            highSeverityMissing.length > 0 ? "text-red-400" : "text-amber-400"
          )} />
          Missing Security Headers
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {missingList.map((header) => {
            const count = missingHeaders[header.key];
            const severityColors = {
              high: "bg-red-500/10 border-red-500/30 text-red-400",
              medium: "bg-amber-500/10 border-amber-500/30 text-amber-400",
              low: "bg-cyan-500/10 border-cyan-500/30 text-cyan-400",
            };

            return (
              <div
                key={header.key}
                className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/30 border border-border"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <XCircle className={cn(
                      "h-4 w-4 flex-shrink-0",
                      header.severity === "high" ? "text-red-400" :
                      header.severity === "medium" ? "text-amber-400" : "text-cyan-400"
                    )} />
                    <span className="font-medium text-sm">{header.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    {header.description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={cn("text-xs", severityColors[header.severity])}>
                    {header.severity}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {count} host{count !== 1 ? "s" : ""}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Raw Headers Toggle Component
function RawHeadersToggle({ allHeaders }: { allHeaders?: Record<string, string> }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  if (!allHeaders || Object.keys(allHeaders).length === 0) return null;

  const handleCopy = async () => {
    const text = Object.entries(allHeaders)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileCode className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">All Response Headers</span>
          <Badge variant="outline" className="text-xs">
            {Object.keys(allHeaders).length} headers
          </Badge>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isExpanded && (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="absolute top-2 right-2 h-7 w-7 p-0"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-400" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
          <div className="p-4 max-h-[300px] overflow-y-auto">
            <div className="space-y-1">
              {Object.entries(allHeaders).map(([key, value]) => (
                <div key={key} className="flex gap-2 text-xs font-mono">
                  <span className="text-cyan-400 min-w-[200px] flex-shrink-0">{key}:</span>
                  <span className="text-muted-foreground break-all">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function HeadersModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: HeadersModuleViewProps) {
  const issues = normalizeIssues(data.issues);
  const hostsScanned = data.hosts_scanned ?? 0;
  const hostsReachable = data.hosts_reachable ?? 0;

  return (
    <ModuleContainer
      moduleName="headers"
      icon={FileCode}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.overall_score}
      grade={data.grade}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Header with grade */}
        <div className="flex items-center gap-6 p-6 rounded-lg bg-muted/30 border border-border">
          {data.grade && <GradeCard grade={data.grade} score={data.overall_score} />}
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">HTTP Security Headers</h3>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div>
                <span className="text-foreground font-medium">{hostsScanned}</span>{" "}
                hosts scanned
              </div>
              <div>
                <span className="text-emerald-400 font-medium">{hostsReachable}</span>{" "}
                reachable
              </div>
              <div>
                <span
                  className={cn(
                    "font-medium",
                    issues.length > 0 ? "text-amber-400" : "text-emerald-400"
                  )}
                >
                  {issues.length}
                </span>{" "}
                issues
              </div>
            </div>
          </div>
        </div>

        {/* Missing Headers Alert */}
        {data.results && data.results.length > 0 && (
          <MissingHeadersAlert results={data.results} />
        )}

        {/* Host Results */}
        {data.results && data.results.length > 0 && (
          <ModuleSection
            title="Host Results"
            icon={Server}
            badge={
              <Badge variant="outline" className="text-xs">
                {data.results.length} hosts
              </Badge>
            }
          >
            <div className="space-y-3">
              {data.results.map((result, index) => (
                <HostResultCard key={index} result={result} />
              ))}
            </div>
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Security Issues"
            badge={
              <Badge
                variant="outline"
                className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
              >
                {issues.length}
              </Badge>
            }
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

HeadersModuleView.displayName = "HeadersModuleView";
